import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { EsitoLoginFinestra } from '@/components/common/EsitoLoginFinestra';
import { useHaptics } from '@/hooks/useHaptics';
import { useRichiedeAccesso, useSceltaOspite } from '@/hooks/useOspite';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * «Accedi», al posto di un contenuto che un OSPITE non puo' vedere (`lib/ospite.ts`).
 *
 * Forma di ADR-0115: il motivo e' un messaggio, e l'azione sta DENTRO il messaggio — non un
 * bottone lasciato solo in mezzo allo schermo, che e' la `<Login />` di prima con un altro nome.
 *
 *  · `schermata` — una rotta intera che chiede un account (Amici, Inbox, Tornei, Impostazioni…);
 *  · `sezione`   — un pannello della Hub: la Hub resta navigabile, cambia solo il pannello;
 *  · `profilo`   — la scheda Profilo dell'ospite, col nome e la foto che manda Telegram.
 *
 * 🔒 Il nome e la foto di `initData` (Q4, decisione del 2026-09-25): SOLO a schermo. Non si
 * mandano a nessun servizio e non diventano un'identita' — farne un'identita' e' un ADR a parte.
 */
export function RichiedeAccesso({ forma = 'schermata' }: { forma?: 'schermata' | 'sezione' | 'profilo' }) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const richiedi = useRichiedeAccesso();
  const { rivedi } = useSceltaOspite();

  return (
    <div className={forma === 'sezione' ? 'py-3' : 'px-4 pb-6 pt-5'} data-testid={`ospite-${forma}`}>
      <div className="rounded-xl border border-border bg-bg-secondary p-4">
        {forma === 'profilo' ? <IdentitaTelegram /> : null}
        <h2 className="text-base font-semibold text-fg">{t(`ospite.${forma}.titolo`)}</h2>
        <p className="mt-1 text-sm text-fg-muted">{t(`ospite.${forma}.corpo`)}</p>
        <Button
          data-tour="ospite-accedi"
          className="mt-4 px-4 py-2 text-sm"
          onClick={() => {
            haptics.impact('light');
            richiedi();
          }}
        >
          {t('ospite.accedi')}
        </Button>
        {import.meta.env.VITE_FEATURE_LOGIN_FINESTRA_ENABLED === 'true' ? (
          <div className="mt-2">
            <EsitoLoginFinestra />
          </div>
        ) : null}
        {/* Sul Profilo ospite la scelta del benvenuto si puo' RIVEDERE: dimenticata la scelta,
            `AppShell` torna a mostrare il benvenuto (C1 di Luca). */}
        {forma === 'profilo' ? (
          <Button variant="ghost" className="mt-2 block px-0 py-1 text-xs" onClick={rivedi}>
            {t('ospite.profilo.rivedi')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function IdentitaTelegram() {
  const { t } = useTranslation('general');
  const { user } = useTelegram();
  if (!user) return null;
  const nome = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return (
    <div className="mb-4 flex items-center gap-3">
      {user.photo_url ? (
        <img src={user.photo_url} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" />
      ) : (
        <div aria-hidden className="h-12 w-12 shrink-0 rounded-full bg-bg" />
      )}
      <div className="min-w-0">
        <p className="truncate text-base font-semibold text-fg">{nome}</p>
        <p className="text-xs text-fg-muted">{t('ospite.profilo.daTelegram')}</p>
      </div>
    </div>
  );
}
